import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import http from "../../../../utils/http";
import { toastMessage, errorToastMessage } from "../../../../utils/toast";
import { LabelStyle } from "../../../Common/styles/form";
import {
  ModalBaseStyles,
  ModalHeader,
  ModalActionButtonStyles,
} from "../../../Common/styles/modal";
import { useParams } from "react-router-dom";

type GroupOption = {
  id: string;
  name: string;
  code: string;
};

export type ChangeGroupModalMode = "assign" | "change";

type Props = {
  openModal: boolean;
  // selected participant row (id = participant UUID)
  participant: any;
  mode: ChangeGroupModalMode;
  closeModal: () => void;
  refreshPage: () => void;
};

const ChangeGroupModal = ({
  openModal,
  participant,
  mode,
  closeModal,
  refreshPage,
}: Props) => {
  const { id: studyId } = useParams();
  const isAssign = mode === "assign";

  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const [toGroupId, setToGroupId] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [randomizationOverride, setRandomizationOverride] =
    useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  // The participant is randomized if a randomization group is assigned.
  const isRandomized = !!(
    participant?.randomization && participant.randomization !== "-"
  );

  const currentGroupName = participant?.cohort || "-";

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setGroupsLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/participant-groups?page=1&size=200`
        );
        const list = res?.data?.data?.data || [];
        setGroups(
          list.map((item: any) => ({
            id: item?.id,
            name: item?.name || "-",
            code: item?.code || "",
          }))
        );
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setGroupsLoading(false);
      }
    };
    if (studyId && openModal) {
      fetchGroups();
    }
  }, [studyId, openModal]);

  const reasonRequired = isRandomized && randomizationOverride;
  const canSubmit =
    !!toGroupId &&
    (!isRandomized || randomizationOverride) &&
    (!reasonRequired || !!reason.trim());

  const submitHandler = async () => {
    try {
      setSubmitLoader(true);
      const payload = {
        toGroupId,
        randomizationOverride: isRandomized ? randomizationOverride : false,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      const url = isAssign
        ? `/study/${studyId}/participant-groups/members/${participant?.id}/assign`
        : `/study/${studyId}/participant-groups/members/${participant?.id}/transfer`;
      const res: AxiosResponse = isAssign
        ? await http.post(url, payload)
        : await http.patch(url, payload);
      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  const groupOptions = isAssign
    ? groups
    : groups.filter((g) => g.name !== currentGroupName);

  return (
    <Modal open={openModal} onClose={closeModal}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "100px" }}>
        <ModalHeader
          title={isAssign ? "Assign User Group" : "Change User Group"}
          onCloseClick={closeModal}
        />

        <Typography sx={{ ...LabelStyle, mb: 2 }} variant="body2">
          {isAssign ? (
            <>
              Assign{" "}
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {participant?.displayId ||
                  participant?.email ||
                  "this participant"}
              </Typography>{" "}
              to:
            </>
          ) : (
            <>
              Move{" "}
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {participant?.displayId ||
                  participant?.email ||
                  "this participant"}
              </Typography>{" "}
              from current group{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                {currentGroupName}
              </Typography>{" "}
              to:
            </>
          )}
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={{ ...LabelStyle, mb: 0.5 }}>Target group</Typography>
          <Select
            size="small"
            value={toGroupId}
            displayEmpty
            disabled={groupsLoading}
            onChange={(e) => setToGroupId(e.target.value)}
          >
            <MenuItem value="" disabled>
              {groupsLoading ? "Loading groups..." : "Select a group"}
            </MenuItem>
            {groupOptions.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
                {g.code ? ` (${g.code})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isRandomized && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ color: "warning.main", mb: 0.5 }}>
              This participant has already been randomized
              {participant?.randomization
                ? ` (${participant.randomization})`
                : ""}
              . {isAssign ? "Assigning" : "Moving"} them requires an explicit
              override and a reason.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={randomizationOverride}
                  onChange={(e) => setRandomizationOverride(e.target.checked)}
                />
              }
              label={
                isAssign
                  ? "Override randomization and assign anyway"
                  : "Override randomization and move anyway"
              }
            />
          </Box>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={{ ...LabelStyle, mb: 0.5 }}>
            Reason{reasonRequired ? " *" : " (optional)"}
          </Typography>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={reason}
            placeholder={
              isAssign
                ? "Reason for assigning the participant"
                : "Reason for moving the participant"
            }
            onChange={(e) => setReason(e.target.value)}
          />
        </FormControl>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {isAssign
            ? "Completed surveys and all other data are retained. The participant will join the selected group."
            : "Completed surveys and all other data are retained. Any not-completed surveys from the current group will be removed."}
        </Typography>

        <Box sx={ModalActionButtonStyles}>
          {!submitLoader ? (
            <>
              <Button variant="outlined" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                onClick={submitHandler}
                disabled={!canSubmit}
              >
                {isAssign ? "Assign" : "Move"}
              </Button>
            </>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangeGroupModal;
